/**
 * Admin dashboard API module.
 *
 * Typed helpers for the admin dashboard stats and recent orders endpoints.
 * Callers (containers, Redux thunks) MUST import from this module — never
 * call the shared axios client directly.
 *
 * Backend dependency note (CON-ADMIN-002): The platform endpoints
 * `GET /api/v1/admin/dashboard/stats` and `GET /api/v1/admin/orders`
 * are defined in the ticket spec but not yet deployed. These functions
 * are typed and exported so containers can call them once the endpoints
 * land (PL-ADMIN-DASHBOARD-001). The thunks guard against 404 responses.
 *
 * CON-ADMIN-002.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminOrdersListDTO,
  DashboardStatsDTO,
} from '@dfq/shared/types/dashboard';

// ---------------------------------------------------------------------------
// Dashboard Stats
// ---------------------------------------------------------------------------

/**
 * Wire shape returned by the platform — every nested key is snake_case
 * while the frontend DTO is camelCase. Projected by {@link getDashboardStats}.
 */
interface WireDashboardStatsResponse {
  kpi: {
    orders_today: number;
    orders_today_prior: number;
    revenue_today: number;
    revenue_today_prior: number;
    revenue_this_week: number;
    revenue_this_week_prior: number;
    revenue_this_month: number;
    revenue_this_month_prior: number;
    pending_prepress_count: number;
    pending_prepress_prior: number;
    open_quote_requests: number;
    open_quote_requests_prior: number;
  };
  orders_by_status: Array<{ status: string; count: number }>;
  low_stock_alerts: Array<{
    material_id: number;
    material_name: string;
    stock_sqft_remaining: number;
    threshold_sqft: number;
  }>;
}

/**
 * Fetch all KPI metrics, orders-by-status chart data, and low-stock alerts.
 *
 * Route: `GET /api/v1/admin/dashboard/stats`
 * Auth:  `auth:console` guard — requires admin or super_admin role.
 */
export async function getDashboardStats(): Promise<DashboardStatsDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<WireDashboardStatsResponse>>(
      '/admin/dashboard/stats',
    );
    const data = unwrap(res.data, res.status);
    const k = data.kpi;
    return {
      kpi: {
        ordersToday: k.orders_today,
        ordersTodayPrior: k.orders_today_prior,
        revenueToday: k.revenue_today,
        revenueTodayPrior: k.revenue_today_prior,
        revenueThisWeek: k.revenue_this_week,
        revenueThisWeekPrior: k.revenue_this_week_prior,
        revenueThisMonth: k.revenue_this_month,
        revenueThisMonthPrior: k.revenue_this_month_prior,
        pendingPrepressCount: k.pending_prepress_count,
        pendingPrePressPrior: k.pending_prepress_prior,
        openQuoteRequests: k.open_quote_requests,
        openQuoteRequestsPrior: k.open_quote_requests_prior,
      },
      ordersByStatus: data.orders_by_status,
      lowStockAlerts: data.low_stock_alerts.map((a) => ({
        materialId: a.material_id,
        materialName: a.material_name,
        stockSqftRemaining: a.stock_sqft_remaining,
        thresholdSqft: a.threshold_sqft,
      })),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Recent Orders
// ---------------------------------------------------------------------------

export interface GetRecentOrdersParams {
  limit?: number;
  sort?: 'recent' | 'oldest';
}

interface AdminOrdersListResponse {
  orders: AdminOrdersListDTO['orders'];
  pagination: AdminOrdersListDTO['pagination'];
}

/**
 * Fetch a paginated list of admin orders.
 *
 * Route: `GET /api/v1/admin/orders`
 * Auth:  `auth:console` guard — requires `orders:admin` Passport scope.
 *
 * Default params for the dashboard: `{ limit: 10, sort: 'recent' }`.
 */
export async function getRecentOrders(
  params: GetRecentOrdersParams = { limit: 10, sort: 'recent' },
): Promise<AdminOrdersListDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<AdminOrdersListResponse>>(
      '/admin/orders',
      { params },
    );
    const data = unwrap(res.data, res.status);
    return {
      orders: data.orders,
      pagination: data.pagination,
    };
  } catch (err) {
    throwAsApiError(err);
  }
}
